import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';

function formatNumber(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function displayName(u) {
  const first = u.firstName?.trim();
  const last = u.lastName?.trim();
  const joined = [first, last].filter(Boolean).join(' ');
  return joined || u.email;
}

function StatCard({ label, value, sub }) {
  return (
    <div className="hxd-card">
      <div className="text-[11px] font-bold uppercase tracking-display-xs text-hxd-muted mb-1">
        {label}
      </div>
      <div className="font-display text-[28px] font-bold tabular-nums">{formatNumber(value)}</div>
      {sub && <p className="text-[12px] text-hxd-muted mt-1 m-0">{sub}</p>}
    </div>
  );
}

export default function AdminReferrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/referrals-overview');
      setData(res);
    } catch (err) {
      setError(err?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = data?.stats || {};
  const milestones = data?.milestones || [];
  const topReferrers = data?.topReferrers || [];

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-[14px] font-bold tracking-display-md uppercase text-hxd-ink">
            Referral Program
          </h2>
          <p className="text-[13px] text-hxd-muted mt-1 max-w-xl">
            Live data from Beehiiv + local signup metrics. Reward tiers themselves are managed in
            the Beehiiv app — this page is read-only.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="hxd-btn hxd-btn-ghost hxd-btn-sm"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <Link to="/referrals" className="hxd-btn hxd-btn-ghost hxd-btn-sm">
            Subscriber view →
          </Link>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-5 text-[13px] text-[#c0392b] bg-[#fdecea] border border-[#f5c6cb] rounded px-4 py-3"
        >
          Couldn&apos;t load overview: {error}
        </div>
      )}

      {data?.beehiivError && (
        <div className="mb-5 text-[12px] text-[#7a5b13] bg-[#fff7e6] border border-[#fde68a] rounded px-4 py-3">
          Beehiiv data is temporarily unavailable ({data.beehiivError.message || data.beehiivError.code}). Local stats still shown.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total referrers"
          value={stats.totalReferrers}
          sub="Local users with ≥1 confirmed referral"
        />
        <StatCard
          label="Signups this month"
          value={stats.signupsThisMonth}
          sub="Users created since the 1st"
        />
        <StatCard
          label="Beehiiv subscribers"
          value={stats.beehiivSubscriberTotal}
          sub="Total active subs per Beehiiv"
        />
        <StatCard
          label="Reward tiers"
          value={stats.rewardTierCount}
          sub="As configured in Beehiiv"
        />
      </div>

      <section className="hxd-card mb-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="hxd-card-header m-0">Top referrers</div>
          <span className="text-[11px] text-hxd-muted">
            {topReferrers.length === 0 ? 'No data yet' : `Top ${topReferrers.length}`}
          </span>
        </div>
        <div className="border border-hxd-border rounded overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-hxd-surface border-b border-hxd-border">
                {['#', 'User', 'Referrals', 'Member since', 'Beehiiv'].map((h, i) => (
                  <th
                    key={h}
                    className={[
                      'font-display text-[10px] font-bold tracking-display-md uppercase px-4 py-2.5',
                      i === 2 ? 'text-right' : 'text-left',
                    ].join(' ')}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-hxd-muted">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && topReferrers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-hxd-muted">
                    No referrers yet
                  </td>
                </tr>
              )}
              {!loading && topReferrers.map((u, i) => (
                <tr
                  key={u.id}
                  className={i % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white'}
                >
                  <td className="px-4 py-2.5 font-mono text-[12px] text-hxd-muted tabular-nums">
                    {i + 1}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-hxd-ink">{displayName(u)}</div>
                    {(u.firstName || u.lastName) && (
                      <div className="text-[11px] text-hxd-muted">{u.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold tabular-nums text-hxd-orange">
                    {u.referralCount}
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-hxd-muted">
                    {formatDate(u.memberSince)}
                  </td>
                  <td className="px-4 py-2.5 text-[11px] font-mono text-hxd-muted">
                    {u.beehiivSubscriberId ? (
                      <span title={u.beehiivSubscriberId}>
                        {u.beehiivSubscriberId.slice(0, 10)}…
                      </span>
                    ) : (
                      <span className="italic">not synced</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="hxd-card">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="hxd-card-header m-0">Reward tiers (read-only)</div>
          <a
            href="https://app.beehiiv.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-semibold text-hxd-orange hover:underline"
          >
            Edit in Beehiiv →
          </a>
        </div>
        {loading && (
          <p className="text-[13px] text-hxd-muted italic">Loading reward tiers…</p>
        )}
        {!loading && milestones.length === 0 && !data?.beehiivError && (
          <p className="text-[13px] text-hxd-muted italic">
            No reward tiers configured in Beehiiv yet. Set them up in the Beehiiv app under
            Settings → Referral Program.
          </p>
        )}
        {!loading && milestones.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {milestones.map((m) => (
              <div
                key={m.id}
                className="bg-hxd-surface border border-hxd-border rounded p-3 text-center flex flex-col"
              >
                {m.reward?.imageUrl ? (
                  <img
                    src={m.reward.imageUrl}
                    alt=""
                    className="w-10 h-10 mx-auto mb-1.5 object-contain"
                  />
                ) : (
                  <div className="text-2xl leading-none mb-1.5">🎁</div>
                )}
                <div className="font-display text-[9px] font-bold tracking-display-xs uppercase text-hxd-subtle mb-0.5 tabular-nums">
                  {m.numReferrals} referral{m.numReferrals === 1 ? '' : 's'}
                </div>
                <div className="text-[12px] font-semibold text-hxd-ink leading-snug">
                  {m.reward?.name || 'Untitled reward'}
                </div>
                {m.reward?.type && (
                  <div className="font-mono text-[9px] tracking-display-xs uppercase text-hxd-muted mt-1">
                    {m.reward.type.replace('_', ' ')}
                  </div>
                )}
                {m.autoFulfill && (
                  <div className="font-mono text-[9px] tracking-display-xs uppercase text-hxd-orange mt-auto pt-1.5">
                    Auto-fulfill
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="text-[12px] text-hxd-muted mt-4">
        To add, edit, or remove reward tiers and their images, log into the Beehiiv app and visit
        Settings → Referral Program. Changes there appear here on the next refresh (cached ~5
        minutes).
      </p>
    </>
  );
}
